import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Tag,
  Rate,
  Divider,
  InputNumber,
  Tabs,
  List,
  Avatar,
  message,
  Spin,
  Breadcrumb,
  Input,
  Empty,
  Image,
  Descriptions,
} from 'antd';
import {
  HeartOutlined,
  ShoppingCartOutlined,
  ShareAltOutlined,
  StarOutlined,
  HeartFilled,
  UserOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  SendOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  ShopOutlined,
  MinusOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  EyeOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import {
  productService,
  cartService,
  wishlistService,
  reviewService,
  storageUrl,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'NPR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price || 0);
};

const CustomerProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [wishlistId, setWishlistId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  const requireAuth = () => {
    if (!isAuthenticated) {
      message.info('Please login to continue');
      navigate('/login', { state: { from: `/customer/products/${id}` } });
      return false;
    }
    return true;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await productService.getProductDetail(id);
        const raw = response.data?.data || response.data;
        const p = {
          ...raw,
          category: typeof raw.category === 'object' ? raw.category?.name || '' : raw.category,
          reviews: Array.isArray(raw.reviews) ? raw.reviews : [],
          images: Array.isArray(raw.images)
            ? raw.images.map((img) => ({ ...img, url: img.url || img.image_url || '' }))
            : [],
          relatedProducts: Array.isArray(raw.relatedProducts) ? raw.relatedProducts : [],
          price: parseFloat(raw.sale_price ?? raw.final_price ?? raw.price) || 0,
          original_price:
            raw.sale_price != null && Number(raw.sale_price) < Number(raw.price)
              ? parseFloat(raw.price)
              : raw.original_price
                ? parseFloat(raw.original_price)
                : null,
          originalPrice: raw.originalPrice ? parseFloat(raw.originalPrice) : null,
          rating: parseFloat(raw.average_rating) || parseFloat(raw.rating) || 0,
          total_reviews: raw.total_reviews || (Array.isArray(raw.reviews) ? raw.reviews.length : 0),
          features: Array.isArray(raw.features) ? raw.features : [],
          specifications: raw.specifications || {},
          provider: raw.provider || null,
          stock: parseInt(raw.stock) || 0,
          is_available: raw.is_available !== false,
        };
        setProduct(p);

        // Track recently viewed
        try {
          const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
          const updated = [
            {
              id: p.id,
              name: p.name,
              price: p.price,
              image: storageUrl(p.images?.[0]?.url) || null,
              timestamp: Date.now(),
            },
            ...viewed.filter((v) => v.id !== p.id),
          ].slice(0, 10);
          localStorage.setItem('recentlyViewed', JSON.stringify(updated));
          setRecentlyViewed(updated.slice(1, 5));
        } catch {
          /* ignore */
        }
      } catch (error) {
        message.error('Failed to load product');
        navigate('/customer/products');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated || !product) return;
    wishlistService
      .getWishlist()
      .then((res) => {
        const items = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const found = items.find(
          (i) => i.product_service_id === id || i.product_service?.id === id,
        );
        if (found) {
          setIsFavorite(true);
          setWishlistId(found.id);
        }
      })
      .catch(() => {});
  }, [id, isAuthenticated, product]);

  const fetchQuestions = async () => {
    try {
      const res = await reviewService.getProductQuestions(id);
      setQuestions(res.data?.data || []);
    } catch {
      /* ignore */
    }
  };

  const handleAddToCart = async () => {
    if (!requireAuth()) return;
    try {
      await cartService.addToCart(id, quantity);
      message.success('Added to cart!');
    } catch {
      message.error('Failed to add to cart');
    }
  };

  const handleBuyNow = async () => {
    if (!requireAuth()) return;
    try {
      await cartService.addToCart(id, quantity);
      navigate('/cart');
    } catch {
      message.error('Failed to add to cart');
    }
  };

  const handleToggleWishlist = async () => {
    if (!requireAuth()) return;
    try {
      if (isFavorite) {
        await wishlistService.removeFromWishlist(wishlistId || id);
        setIsFavorite(false);
        message.success('Removed from wishlist');
      } else {
        await wishlistService.addToWishlist(id);
        setIsFavorite(true);
        message.success('Added to wishlist!');
      }
    } catch {
      message.error('Failed to update wishlist');
    }
  };

  const handleShare = () => {
    navigator.clipboard?.writeText(window.location.href);
    message.success('Link copied to clipboard!');
  };

  const handleAskQuestion = async () => {
    if (!requireAuth() || !newQuestion.trim()) return;
    setSubmittingQuestion(true);
    try {
      await reviewService.askProductQuestion(id, newQuestion.trim());
      message.success('Question submitted!');
      setNewQuestion('');
      fetchQuestions();
    } catch {
      message.error('Failed to submit question');
    } finally {
      setSubmittingQuestion(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!requireAuth()) return;
    setSubmittingReview(true);
    try {
      await reviewService.createReview({
        product_service_id: id,
        rating: reviewRating,
        comment: reviewComment,
      });
      message.success('Review submitted!');
      setShowReviewForm(false);
      setReviewComment('');
      setReviewRating(5);
      const res = await productService.getProductDetail(id);
      const raw = res.data?.data || res.data;
      if (Array.isArray(raw.reviews)) setProduct((prev) => ({ ...prev, reviews: raw.reviews }));
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  if (!product) return null;

  const allImages =
    product.images?.length > 0
      ? product.images
      : [
          {
            url: `https://placehold.co/600x400/e2e8f0/64748b?text=${encodeURIComponent(product.name)}`,
          },
        ];

  const discountPercent =
    product.original_price > product.price
      ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
      : 0;

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/dashboard">Dashboard</Link> },
          { title: <Link to="/customer/products">Products</Link> },
          { title: product.name },
        ]}
      />

      <Row gutter={[24, 24]}>
        {/* Left Column, Image Gallery */}
        <Col xs={24} md={12} lg={13}>
          <Card
            style={{ borderRadius: 16, border: '1px solid #e8f0e8', overflow: 'hidden' }}
            styles={{ body: { padding: 0 } }}
          >
            <div
              style={{
                position: 'relative',
                background: '#fafafa',
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image
                src={storageUrl(allImages[selectedImage]?.url)}
                alt={product.name}
                style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }}
                fallback="https://placehold.co/600x400/e2e8f0/64748b?text=No+Image"
                preview={{ mask: <EyeOutlined style={{ fontSize: 20 }} /> }}
              />
              {discountPercent > 0 && (
                <Tag
                  color="gold"
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    fontSize: 14,
                    padding: '4px 12px',
                    borderRadius: 8,
                  }}
                >
                  -{discountPercent}% OFF
                </Tag>
              )}
              {product.type === 'service' && (
                <Tag
                  color="blue"
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    fontSize: 14,
                    padding: '4px 12px',
                    borderRadius: 8,
                  }}
                >
                  Service
                </Tag>
              )}
            </div>
            {allImages.length > 1 && (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  padding: 12,
                  overflowX: 'auto',
                  background: '#fafafa',
                }}
              >
                {allImages.map((img, i) => (
                  <div
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: 8,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      flexShrink: 0,
                      border: selectedImage === i ? '2px solid #52b788' : '2px solid transparent',
                      opacity: selectedImage === i ? 1 : 0.6,
                      transition: 'all 0.2s',
                    }}
                  >
                    <img
                      src={storageUrl(img.url)}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.src = 'https://placehold.co/64x64/e2e8f0/64748b?text=N';
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>

        {/* Right Column, Product Info */}
        <Col xs={24} md={12} lg={11}>
          <Card
            style={{ borderRadius: 16, border: '1px solid #e8f0e8' }}
            styles={{ body: { padding: 24 } }}
          >
            <Space style={{ marginBottom: 12 }} wrap>
              <Tag color="blue" style={{ borderRadius: 8 }}>
                {product.category || 'General'}
              </Tag>
              {product.is_available ? (
                <Tag color="green" style={{ borderRadius: 8 }}>
                  <CheckCircleOutlined /> In Stock
                </Tag>
              ) : (
                <Tag color="red" style={{ borderRadius: 8 }}>
                  Out of Stock
                </Tag>
              )}
              {product.stock > 0 && product.stock <= 5 && (
                <Tag color="gold" style={{ borderRadius: 8 }}>
                  Only {product.stock} left
                </Tag>
              )}
            </Space>

            <Title level={3} style={{ color: '#2d5a3d', margin: '0 0 12px 0', lineHeight: 1.3 }}>
              {product.name}
            </Title>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <Rate disabled defaultValue={product.rating} style={{ fontSize: 16 }} />
              <Text strong style={{ color: '#2d5a3d' }}>
                {product.rating?.toFixed(1)}
              </Text>
              <Text style={{ color: '#9ca3af' }}>
                ({product.total_reviews || product.reviews?.length || 0} reviews)
              </Text>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <Text style={{ fontSize: 32, fontWeight: 700, color: '#2d5a3d' }}>
                  {formatPrice(product.price)}
                </Text>
                {product.original_price > product.price && (
                  <>
                    <Text delete style={{ fontSize: 18, color: '#9ca3af' }}>
                      {formatPrice(product.original_price)}
                    </Text>
                    <Tag color="gold" style={{ borderRadius: 8 }}>
                      -{discountPercent}%
                    </Tag>
                  </>
                )}
              </div>
              {product.type !== 'service' && (
                <Text style={{ color: '#6b7280', fontSize: 13, display: 'block', marginTop: 4 }}>
                  <SafetyCertificateOutlined style={{ color: '#52b788', marginRight: 4 }} />
                  Inclusive of all taxes
                </Text>
              )}
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Short Description */}
            <Paragraph
              style={{ color: '#4b5563', marginBottom: 16 }}
              ellipsis={{ rows: 3, expandable: true }}
            >
              {product.description}
            </Paragraph>

            {/* Quantity Selector */}
            {product.type !== 'service' && (
              <div style={{ marginBottom: 20 }}>
                <Text strong style={{ color: '#374151', display: 'block', marginBottom: 8 }}>
                  Quantity
                </Text>
                <Space>
                  <Button
                    shape="circle"
                    icon={<MinusOutlined />}
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    style={{ borderColor: '#52b788', color: '#52b788' }}
                  />
                  <InputNumber
                    min={1}
                    max={product.stock || 99}
                    value={quantity}
                    onChange={setQuantity}
                    style={{ width: 60, textAlign: 'center', borderRadius: 8 }}
                  />
                  <Button
                    shape="circle"
                    icon={<PlusOutlined />}
                    onClick={() => setQuantity(Math.min(product.stock || 99, quantity + 1))}
                    disabled={quantity >= product.stock}
                    style={{ borderColor: '#52b788', color: '#52b788' }}
                  />
                </Space>
              </div>
            )}

            {/* Action Buttons */}
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <Row gutter={12}>
                <Col span={12}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ShoppingCartOutlined />}
                    onClick={handleAddToCart}
                    disabled={!product.is_available}
                    block
                    style={{
                      height: 48,
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                      border: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Add to Cart
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    size="large"
                    icon={<ThunderboltOutlined />}
                    onClick={handleBuyNow}
                    disabled={!product.is_available}
                    block
                    style={{
                      height: 48,
                      borderRadius: 10,
                      border: '2px solid #52b788',
                      color: '#2d5a3d',
                      fontWeight: 600,
                    }}
                  >
                    Buy Now
                  </Button>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Button
                    icon={isFavorite ? <HeartFilled /> : <HeartOutlined />}
                    onClick={handleToggleWishlist}
                    block
                    style={{
                      borderRadius: 10,
                      color: isFavorite ? '#e5484d' : '#6b7280',
                      borderColor: '#d1d5db',
                    }}
                  >
                    {isFavorite ? 'Saved' : 'Save to Wishlist'}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    icon={<ShareAltOutlined />}
                    onClick={handleShare}
                    block
                    style={{ borderRadius: 10, borderColor: '#d1d5db' }}
                  >
                    Share
                  </Button>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={24}>
                  <Button
                    icon={<MessageOutlined />}
                    onClick={() => {
                      if (!requireAuth()) return;
                      navigate('/chat', {
                        state: {
                          contactId: product.provider_id,
                          contactName:
                            product.provider?.name || product.provider_name || 'Provider',
                        },
                      });
                    }}
                    block
                    style={{
                      borderRadius: 10,
                      borderColor: '#52b788',
                      color: '#2d5a3d',
                    }}
                  >
                    Inquiry
                  </Button>
                </Col>
              </Row>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Tabs Section, Description, Reviews, Q&A */}
      <Row gutter={24} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16}>
          <Card style={{ borderRadius: 16, border: '1px solid #e8f0e8' }}>
            <Tabs
              defaultActiveKey="description"
              items={[
                {
                  key: 'description',
                  label: 'Description',
                  children: (
                    <div>
                      <Paragraph style={{ color: '#4b5563', fontSize: 15, lineHeight: 1.8 }}>
                        {product.description}
                      </Paragraph>
                      {product.features?.length > 0 && (
                        <>
                          <Title level={5} style={{ color: '#2d5a3d', marginTop: 24 }}>
                            Key Features
                          </Title>
                          <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
                            {product.features.map((f, i) => (
                              <li key={i}>
                                <Text style={{ color: '#4b5563' }}>
                                  {typeof f === 'object' ? f.name || f.value : f}
                                </Text>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      {Object.keys(product.specifications || {}).length > 0 && (
                        <>
                          <Title level={5} style={{ color: '#2d5a3d', marginTop: 24 }}>
                            Specifications
                          </Title>
                          <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
                            {Object.entries(product.specifications).map(([key, val]) => (
                              <Descriptions.Item key={key} label={key}>
                                {String(val)}
                              </Descriptions.Item>
                            ))}
                          </Descriptions>
                        </>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'reviews',
                  label: `Reviews (${product.reviews?.length || 0})`,
                  children: (
                    <div>
                      <div
                        style={{
                          marginBottom: 16,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 12,
                        }}
                      >
                        <div>
                          <Text strong style={{ fontSize: 20, color: '#2d5a3d' }}>
                            {product.rating?.toFixed(1)}
                          </Text>
                          <Rate
                            disabled
                            defaultValue={product.rating}
                            style={{ fontSize: 14, marginLeft: 8 }}
                          />
                        </div>
                        <Button
                          type="primary"
                          icon={<StarOutlined />}
                          onClick={() => {
                            if (requireAuth()) setShowReviewForm(!showReviewForm);
                          }}
                          style={{
                            borderRadius: 10,
                            background: '#52b788',
                            borderColor: '#52b788',
                          }}
                        >
                          {showReviewForm ? 'Cancel' : 'Write a Review'}
                        </Button>
                      </div>
                      {showReviewForm && (
                        <Card
                          size="small"
                          style={{
                            marginBottom: 16,
                            background: '#fafff5',
                            border: '1px solid #e8f0e8',
                            borderRadius: 12,
                          }}
                        >
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <div>
                              <Text strong>Your Rating: </Text>
                              <Rate
                                value={reviewRating}
                                onChange={setReviewRating}
                                style={{ fontSize: 20 }}
                              />
                            </div>
                            <Input.TextArea
                              rows={3}
                              placeholder="Share your experience with this product..."
                              value={reviewComment}
                              onChange={(e) => setReviewComment(e.target.value)}
                              style={{ borderRadius: 8 }}
                            />
                            <Button
                              type="primary"
                              loading={submittingReview}
                              onClick={handleSubmitReview}
                              icon={<SendOutlined />}
                              disabled={!reviewRating}
                              style={{
                                borderRadius: 10,
                                background: '#52b788',
                                borderColor: '#52b788',
                                alignSelf: 'flex-end',
                              }}
                            >
                              Submit Review
                            </Button>
                          </Space>
                        </Card>
                      )}
                      {product.reviews?.length > 0 ? (
                        <List
                          dataSource={product.reviews}
                          renderItem={(review) => (
                            <List.Item style={{ padding: '16px 0' }}>
                              <List.Item.Meta
                                avatar={
                                  <Avatar
                                    icon={<UserOutlined />}
                                    style={{ backgroundColor: '#52b788' }}
                                  />
                                }
                                title={
                                  <Space>
                                    <Text strong style={{ color: '#2d5a3d' }}>
                                      {review.user?.name ||
                                        review.user?.full_name ||
                                        review.user_name ||
                                        review.customer_name ||
                                        'Anonymous'}
                                    </Text>
                                    <Rate
                                      disabled
                                      defaultValue={parseFloat(review.rating)}
                                      style={{ fontSize: 12 }}
                                    />
                                  </Space>
                                }
                                description={
                                  <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      {dayjs(review.date || review.created_at).format(
                                        'MMM D, YYYY',
                                      )}
                                    </Text>
                                    <Paragraph style={{ margin: '4px 0 0', color: '#4b5563' }}>
                                      {review.comment || review.review}
                                    </Paragraph>
                                    {review.images?.length > 0 && (
                                      <Space style={{ marginTop: 8 }}>
                                        {review.images.map((img, i) => (
                                          <Image
                                            key={i}
                                            src={storageUrl(img.url || img)}
                                            width={60}
                                            height={60}
                                            style={{ borderRadius: 8, objectFit: 'cover' }}
                                          />
                                        ))}
                                      </Space>
                                    )}
                                  </div>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      ) : (
                        <Empty description="No reviews yet. Be the first to review!" />
                      )}
                    </div>
                  ),
                },
                {
                  key: 'qa',
                  label: `Q&A (${questions.length})`,
                  children: (
                    <div>
                      {isAuthenticated && (
                        <Card
                          size="small"
                          style={{
                            marginBottom: 16,
                            background: '#fafff5',
                            border: '1px solid #e8f0e8',
                            borderRadius: 12,
                          }}
                        >
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <Text strong style={{ color: '#2d5a3d' }}>
                              Have a question?
                            </Text>
                            <Input.TextArea
                              rows={2}
                              placeholder="Ask a question about this product..."
                              value={newQuestion}
                              onChange={(e) => setNewQuestion(e.target.value)}
                              style={{ borderRadius: 8 }}
                            />
                            <Button
                              type="primary"
                              icon={<QuestionCircleOutlined />}
                              loading={submittingQuestion}
                              onClick={handleAskQuestion}
                              disabled={!newQuestion.trim()}
                              style={{
                                borderRadius: 10,
                                background: '#52b788',
                                borderColor: '#52b788',
                                alignSelf: 'flex-end',
                              }}
                            >
                              Submit Question
                            </Button>
                          </Space>
                        </Card>
                      )}
                      {questions.length > 0 ? (
                        <List
                          dataSource={questions}
                          renderItem={(q) => (
                            <List.Item style={{ padding: '16px 0' }}>
                              <List.Item.Meta
                                avatar={
                                  <Avatar
                                    icon={<UserOutlined />}
                                    style={{ backgroundColor: '#52b788' }}
                                  />
                                }
                                title={
                                  <Space>
                                    <Text strong style={{ color: '#2d5a3d' }}>
                                      {q.customer?.name ||
                                        q.customer?.full_name ||
                                        q.user?.name ||
                                        q.user?.full_name ||
                                        'Customer'}
                                    </Text>
                                    <Text style={{ fontSize: 12, color: '#9ca3af' }}>asked</Text>
                                  </Space>
                                }
                                description={
                                  <div>
                                    <Paragraph style={{ marginBottom: 8, color: '#4b5563' }}>
                                      {q.question || q.comment}
                                    </Paragraph>
                                    {q.answer ? (
                                      <Card
                                        size="small"
                                        style={{
                                          background: '#f0faf0',
                                          border: '1px solid #d1fae5',
                                          borderRadius: 8,
                                          marginTop: 8,
                                        }}
                                      >
                                        <Space>
                                          <CheckCircleOutlined style={{ color: '#52b788' }} />
                                          <Text strong style={{ color: '#2d5a3d' }}>
                                            Answer:
                                          </Text>
                                        </Space>
                                        <Paragraph style={{ margin: '4px 0 0', color: '#374151' }}>
                                          {q.answer}
                                        </Paragraph>
                                        {q.answeredBy?.name && (
                                          <Text style={{ fontSize: 11, color: '#9ca3af' }}>
                                            , {q.answeredBy.name}
                                          </Text>
                                        )}
                                      </Card>
                                    ) : (
                                      <Tag
                                        icon={<ClockCircleOutlined />}
                                        color="gold"
                                        style={{ fontSize: 12, borderRadius: 6 }}
                                      >
                                        Awaiting provider response
                                      </Tag>
                                    )}
                                  </div>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      ) : (
                        <Empty description="No questions yet. Be the first to ask!" />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>

        {/* Right Sidebar, Provider Info, Recently Viewed */}
        <Col xs={24} lg={8}>
          {/* Provider Card */}
          {product.provider && (
            <Card style={{ borderRadius: 16, border: '1px solid #e8f0e8', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <Avatar size={48} icon={<UserOutlined />} style={{ backgroundColor: '#52b788' }} />
                <div>
                  <Text strong style={{ color: '#2d5a3d', display: 'block' }}>
                    {product.provider.name || 'Provider'}
                  </Text>
                  <Rate
                    disabled
                    defaultValue={product.provider.average_rating || 4.5}
                    style={{ fontSize: 12 }}
                  />
                </div>
              </div>
              {product.provider.phone && (
                <Text style={{ color: '#6b7280', display: 'block', marginBottom: 4 }}>
                  <PhoneOutlined style={{ marginRight: 6 }} />
                  {product.provider.phone}
                </Text>
              )}
              {product.provider.email && (
                <Text style={{ color: '#6b7280', display: 'block', marginBottom: 4 }}>
                  <MailOutlined style={{ marginRight: 6 }} />
                  {product.provider.email}
                </Text>
              )}
              {product.provider.address && (
                <Text style={{ color: '#6b7280', display: 'block' }}>
                  <EnvironmentOutlined style={{ marginRight: 6 }} />
                  {product.provider.address}
                </Text>
              )}
            </Card>
          )}

          {/* Product Details Card */}
          <Card
            title={
              <Text strong style={{ color: '#2d5a3d' }}>
                Product Details
              </Text>
            }
            style={{ borderRadius: 16, border: '1px solid #e8f0e8', marginBottom: 16 }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Category">
                {product.category || 'General'}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {product.type === 'service' ? 'Service' : 'Product'}
              </Descriptions.Item>
              {product.stock > 0 && (
                <Descriptions.Item label="Stock">{product.stock} units</Descriptions.Item>
              )}
              {product.sku && <Descriptions.Item label="SKU">{product.sku}</Descriptions.Item>}
              {product.brand && (
                <Descriptions.Item label="Brand">
                  {product.brand?.name || product.brand}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Recently Viewed */}
          {recentlyViewed.length > 0 && (
            <Card
              title={
                <Text strong style={{ color: '#2d5a3d' }}>
                  Recently Viewed
                </Text>
              }
              style={{ borderRadius: 16, border: '1px solid #e8f0e8' }}
            >
              <List
                dataSource={recentlyViewed}
                renderItem={(item) => (
                  <List.Item
                    onClick={() => navigate(`/customer/products/${item.id}`)}
                    style={{ cursor: 'pointer', padding: '8px 0' }}
                  >
                    <List.Item.Meta
                      avatar={
                        <div
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 8,
                            overflow: 'hidden',
                            background: '#f0faf0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <ShopOutlined style={{ color: '#52b788' }} />
                          )}
                        </div>
                      }
                      title={<Text style={{ fontSize: 13, color: '#2d5a3d' }}>{item.name}</Text>}
                      description={
                        <Text style={{ fontSize: 12, color: '#52b788' }}>
                          {formatPrice(item.price)}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default CustomerProductDetail;
